/**
 * useManualEntryHandlers Hook
 * Shared hook for manual CPT and delivery charge entry handling
 */

import { useState, useMemo, useCallback } from "react";
import { CPTSlabForManualOption } from "../../../views/Orders/enums";
import { ManualDeliveryOptionEntry } from "../types";
import {
  minutesToHours,
  calculateFutureDate,
  parseQuickEtaMins,
} from "../utils/deliveryTimeUtils";
import {
  dateSecondisBeforeDateFirst,
  minusFiveMinutesFromCurrentTime,
} from "../../../utils/dateResolvers";

export interface QuickEtaOption {
  label: string;
  minutes: number;
}

interface UseManualEntryHandlersProps {
  isExpired: boolean;
  quickEtasMins?: string;
  currentManualEntry?: ManualDeliveryOptionEntry;
  onManualEntryChange: (entry: ManualDeliveryOptionEntry) => void;
}

export function useManualOptionHandlers({
  isExpired,
  quickEtasMins,
  currentManualEntry,
  onManualEntryChange,
}: UseManualEntryHandlersProps) {
  // Reference time for validation (set once on mount)
  const [cptGivenTime] = useState(() => minusFiveMinutesFromCurrentTime());

  // Parse quick ETA options from config
  const quickEtaOptions: QuickEtaOption[] = useMemo(() => {
    const minutesList = parseQuickEtaMins(quickEtasMins);
    return minutesList.map((minutes) => ({
      label: minutesToHours(minutes),
      minutes: minutes,
    }));
  }, [quickEtasMins]);

  // Handle CPT change with validation
  const handleManualCPTChange = useCallback(
    (value: string) => {
      if (value && dateSecondisBeforeDateFirst(cptGivenTime, value)) {
        alert("ETA time can not be less than ETA given time");
        return;
      }
      const current = currentManualEntry || {
        cpt: "",
        deliveryCharge: 0,
      };
      onManualEntryChange({ ...current, cpt: value });
    },
    [cptGivenTime, currentManualEntry, onManualEntryChange]
  );

  // Handle delivery charge change
  const handleManualChargeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(event.target.value, 10) || 0;
      const current = currentManualEntry || {
        cpt: "",
        deliveryCharge: 0,
      };
      onManualEntryChange({ ...current, deliveryCharge: value });
    },
    [currentManualEntry, onManualEntryChange],
  );

  const handleManualCPTSlabChange = useCallback(
    (value: CPTSlabForManualOption) => {
      const current = currentManualEntry || {
        cpt: "",
        deliveryCharge: 0,
      };
      onManualEntryChange({ ...current, cptSlab: value });
    },
    [currentManualEntry, onManualEntryChange],
  );

  // Handle quick ETA tag selection
  const handleQuickEtaSelect = useCallback(
    (minutes: number) => {
      if (isExpired) return;
      const futureDateStr = calculateFutureDate(minutes);
      handleManualCPTChange(futureDateStr);
    },
    [isExpired, handleManualCPTChange]
  );

  return {
    cptGivenTime,
    quickEtaOptions,
    handleManualCPTChange,
    handleManualChargeChange,
    handleQuickEtaSelect,
    handleManualCPTSlabChange,
  };
}
